import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { usePoll } from "../lib/hooks";
import { Page, Panel, Kpi } from "../components/Layout";
import { CATEGORY_LABEL, REC_STATUS, REVIEW_LABEL } from "../lib/labels";

function Bars({ data, labels, testid, categoryLinks = false }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map((e) => e[1]));
  if (!entries.length) return <div className="text-sm text-neutral-500">Nessun dato.</div>;
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {entries.map(([k, v]) => {
        const content = (
          <>
            <span className="truncate">{labels?.[k] || k}</span>
            <div className="h-2 bg-neutral-100"><div className="h-2 bg-[#0047FF]" style={{ width: `${(v / max) * 100}%` }} /></div>
            <span className="text-right tabular-nums">{v}</span>
          </>
        );
        const rowClass = "grid grid-cols-[180px_1fr_40px] items-center gap-3 text-xs";
        return categoryLinks ? (
          <Link
            key={k}
            to={`/interessati?scope=all&category=${encodeURIComponent(k)}`}
            className={`${rowClass} cursor-pointer rounded-sm hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0047FF]`}
            title={`Apri ${labels?.[k] || k}`}
          >
            {content}
          </Link>
        ) : (
          <div key={k} className={rowClass}>{content}</div>
        );
      })}
    </div>
  );
}

export default function Dashboard() {
  const [s, setS] = useState(null);
  usePoll(() => api.get("/dashboard").then((r) => setS(r.data)).catch(() => {}), 10000);
  return (
    <Page title="Dashboard" subtitle="Panoramica in tempo reale di acquisizioni, elaborazioni AI locali e opportunità da verificare." testid="dashboard-page">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <Kpi label="Clienti" value={s?.customers ?? "—"} testid="kpi-customers" />
        <Kpi label="Registrazioni" value={s?.recordings ?? "—"} testid="kpi-recordings" />
        <Kpi label="Opportunità" value={s?.opportunities ?? "—"} testid="kpi-opportunities" accent />
        <Kpi label="Da verificare" value={(s?.review?.pending || 0) + (s?.review?.needs_review || 0)} testid="kpi-pending-review" />
        <Kpi label="Job attivi" value={s?.jobs_active ?? "—"} testid="kpi-jobs-active" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title="Classificazioni AI"><Bars data={s?.categories} labels={CATEGORY_LABEL} testid="chart-categories" categoryLinks /></Panel>
        <Panel title="Stato registrazioni"><Bars data={s?.recording_status} labels={REC_STATUS} testid="chart-status" /></Panel>
        <Panel title="Revisione opportunità"><Bars data={s?.review} labels={REVIEW_LABEL} testid="chart-review" /></Panel>
      </div>
      <Panel title="Flusso operativo" className="mt-4">
        <ol className="grid md:grid-cols-5 gap-3 text-sm">
          {[["1. Configura", "/clienti", "Cliente, predictive e server registrazioni"], ["2. Cerca", "/ricerca", "Ultimi 5 giorni, campagne ed esiti"],
            ["3. Acquisisci e analizza", "/elaborazioni", "Trascrizione e classificazione locale"], ["4. Verifica", "/interessati", "Approva o rifiuta le opportunità"],
            ["5. Esporta", "/report", "Excel/CSV per il cliente"]].map(([t, to, d]) => (
            <Link key={t} to={to} className="border rounded-sm p-3 hover:border-[#0047FF] transition-colors" data-testid={`flow-${to.slice(1)}`}>
              <div className="font-medium">{t}</div><div className="text-xs text-neutral-500 mt-1">{d}</div>
            </Link>
          ))}
        </ol>
        {s?.errors_7d > 0 && <p className="text-xs text-red-700 mt-3" data-testid="dashboard-errors">{s.errors_7d} errori negli ultimi 7 giorni · <Link to="/log" className="underline">vedi log</Link></p>}
      </Panel>
    </Page>
  );
}
